import React, { useEffect, useState, useCallback, useMemo } from "react";
import styled from "styled-components";
import { Input, Spin, Alert, Tag, Table, Select, Tooltip } from "antd";
import {
  SearchOutlined,
  ReloadOutlined,
  PhoneOutlined,
  InstagramOutlined,
  CrownOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import customClientAxios from "../customAxios";
import { AdminLeads, AdminCrmDashboard } from "../utils/ApiEndpoints";
import LeadDrawer from "./LeadDrawer";

// ── Styled ────────────────────────────────────────────────────────────────

const Page = styled.div`
  padding: 24px 32px 64px;
  background: #f8fafc;
  min-height: 100vh;
`;

const Header = styled.div`
  margin-bottom: 20px;
  h1 {
    margin: 0 0 4px;
    font-size: 24px;
    font-weight: 700;
    color: #0f172a;
    letter-spacing: -0.4px;
  }
  p {
    margin: 0;
    font-size: 13px;
    color: #64748b;
  }
`;

const StatsStrip = styled.div`
  display: flex;
  gap: 10px;
  margin-bottom: 16px;
  flex-wrap: wrap;
`;

const StatPill = styled.div`
  padding: 10px 16px;
  background: white;
  border: 1px solid ${(p) => p.$border || "#e2e8f0"};
  border-radius: 10px;
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 12.5px;
  cursor: pointer;
  transition: all 0.15s ease;
  box-shadow: 0 1px 2px rgba(0,0,0,0.03);

  ${(p) =>
    p.$active &&
    `
    border-color: ${p.$accent};
    background: ${p.$accent}10;
    box-shadow: 0 0 0 2px ${p.$accent}25;
  `}

  &:hover { border-color: ${(p) => p.$accent || "#cbd5e1"}; }

  .dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: ${(p) => p.$accent || "#94a3b8"};
  }
  .label { color: #64748b; }
  .value { font-weight: 700; color: ${(p) => p.$accent || "#0f172a"}; font-size: 15px; }
`;

const FilterBar = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
  margin-bottom: 14px;
  flex-wrap: wrap;
`;

const TableCard = styled.div`
  background: white;
  border-radius: 14px;
  border: 1px solid #e2e8f0;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0,0,0,0.04);
`;

const NameCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  .name { font-size: 13px; font-weight: 600; color: #0f172a; }
  .sub  { font-size: 11px; color: #94a3b8; }
`;

const ContactCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
  font-size: 12px;
  color: #475569;
  svg { font-size: 11px; color: #94a3b8; margin-right: 4px; }
`;

// ── Groups for Post-Onboard ──────────────────────────────────────────────
// Each group is either a stage or a stage+activationPhase combo.
// Filter logic happens client-side on the loaded page since the listLeads
// endpoint doesn't support activationPhase filtering yet.

const GROUPS = [
  { id: "all",           label: "All Post-Onboard", stages: ["won","activated","purchased"], accent: "#475569" },
  { id: "onboarded",     label: "Onboarded",        stages: ["won"],                          accent: "#3b82f6", description: "Signed up · no brand page yet" },
  { id: "brand_page",    label: "Brand Page",       stages: ["activated"], phase: "brand_page",    accent: "#6366f1", description: "Created brand page · no event yet" },
  { id: "event_created", label: "Event Created",    stages: ["activated"], phase: "event_created", accent: "#8b5cf6", description: "Published event · no sale yet" },
  { id: "first_sale",    label: "First Sale",       stages: ["activated"], phase: "first_sale",    accent: "#f59e0b", description: "Sold tickets · no paid plan" },
  { id: "purchased",     label: "Plan Purchased",   stages: ["purchased"],                    accent: "#059669", description: "Paid Growezy customer" },
];

const STAGE_BADGE = {
  won:        { color: "blue",   label: "Onboarded" },
  activated:  { color: "orange", label: "Activated" },
  purchased:  { color: "green",  label: "Plan Purchased" },
};

const PHASE_BADGE = {
  brand_page:    { color: "geekblue", label: "BP" },
  event_created: { color: "purple",   label: "Event" },
  first_sale:    { color: "orange",   label: "Sale" },
  none:          { color: "default",  label: "—" },
};

const relativeDays = (date) => {
  if (!date) return "—";
  const diff = dayjs().diff(dayjs(date), "day");
  if (diff === 0) return "today";
  if (diff === 1) return "1d ago";
  if (diff < 30) return `${diff}d ago`;
  if (diff < 365) return `${Math.floor(diff / 30)}mo ago`;
  return `${Math.floor(diff / 365)}y ago`;
};

// ── Component ─────────────────────────────────────────────────────────────

const PostOnboardBoard = () => {
  const [leads, setLeads] = useState([]);
  const [funnel, setFunnel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [activeGroup, setActiveGroup] = useState("all");
  const [pagination, setPagination] = useState({ current: 1, pageSize: 50, total: 0 });
  const [selectedLeadId, setSelectedLeadId] = useState(null);

  const group = useMemo(
    () => GROUPS.find((g) => g.id === activeGroup) || GROUPS[0],
    [activeGroup]
  );

  // Build query params from active group
  const queryParams = useMemo(() => {
    const p = { stage: group.stages.join(",") };
    return p;
  }, [group]);

  const fetchPage = useCallback(async (page = 1, pageSize = 50, term = search) => {
    setLoading(true);
    setError(null);
    try {
      const params = { ...queryParams, page, limit: pageSize };
      if (term?.trim()) params.search = term.trim();
      // For phase-specific groups, request the whole stage and filter client-side
      // since listLeads doesn't support activationPhase filtering server-side.
      const wantsPhase = !!group.phase;
      if (wantsPhase) {
        params.limit = 1000;
        params.page = 1;
      }

      const r = await customClientAxios().get(AdminLeads, { params });
      let rows = r.data?.data?.leads || [];
      let total = r.data?.data?.pagination?.total || 0;

      if (wantsPhase) {
        rows = rows.filter((l) => l.activationPhase === group.phase);
        total = rows.length;
        // Apply manual paging
        const start = (page - 1) * pageSize;
        rows = rows.slice(start, start + pageSize);
      }

      setLeads(rows);
      setPagination({ current: page, pageSize, total });
    } catch (e) {
      setError(e?.response?.data?.message || e.message || "Failed to load contacts");
    } finally {
      setLoading(false);
    }
  }, [queryParams, group, search]);

  const fetchFunnel = useCallback(async () => {
    try {
      const r = await customClientAxios().get(`${AdminCrmDashboard}?window=all`);
      setFunnel(r.data?.data?.funnel || null);
    } catch {/* non-fatal */}
  }, []);

  useEffect(() => { fetchFunnel(); }, [fetchFunnel]);
  useEffect(() => { fetchPage(1, pagination.pageSize); /* eslint-disable-next-line */ }, [activeGroup]);

  const groupTotals = useMemo(() => {
    if (!funnel) return {};
    const ap = funnel.activated_phases || {};
    return {
      onboarded:     funnel.onboarded || 0,
      brand_page:    ap.brand_page || 0,
      event_created: ap.event_created || 0,
      first_sale:    ap.first_sale || 0,
      purchased:     funnel.purchased || 0,
      all:           (funnel.onboarded || 0) + (funnel.activated || 0) + (funnel.purchased || 0),
    };
  }, [funnel]);

  const columns = [
    {
      title: "Name",
      key: "name",
      render: (_, lead) => (
        <NameCell>
          <span className="name">{lead.name || "(no name)"}</span>
          {lead.city && <span className="sub">{lead.city}</span>}
        </NameCell>
      ),
      sorter: (a, b) => (a.name || "").localeCompare(b.name || ""),
    },
    {
      title: "Contact",
      key: "contact",
      render: (_, lead) => (
        <ContactCell>
          {lead.phone && <div><PhoneOutlined /> {lead.phone}</div>}
          {lead.email && <div>✉ {lead.email}</div>}
        </ContactCell>
      ),
    },
    {
      title: "Stage / Phase",
      key: "stage",
      width: 200,
      render: (_, lead) => {
        const stageB = STAGE_BADGE[lead.stage] || { color: "default", label: lead.stage };
        const phaseB = lead.activationPhase ? PHASE_BADGE[lead.activationPhase] : null;
        return (
          <span>
            <Tag color={stageB.color} style={{ margin: 0 }}>{stageB.label}</Tag>
            {phaseB && lead.stage === "activated" && (
              <Tag color={phaseB.color} style={{ marginLeft: 4 }}>{phaseB.label}</Tag>
            )}
            {lead.stage === "purchased" && (
              <Tag color="gold" style={{ marginLeft: 4 }}>
                <CrownOutlined style={{ fontSize: 10 }} />
              </Tag>
            )}
          </span>
        );
      },
    },
    {
      title: "Source",
      dataIndex: "source",
      key: "source",
      width: 110,
      render: (s) => <Tag style={{ margin: 0, textTransform: "capitalize" }}>{s || "—"}</Tag>,
    },
    {
      title: "IG",
      key: "ig",
      width: 60,
      render: (_, lead) =>
        lead.hasLinkedInstagram ? (
          <Tag color="magenta" style={{ margin: 0 }}>
            <InstagramOutlined style={{ fontSize: 10 }} />
          </Tag>
        ) : (
          <span style={{ color: "#cbd5e1" }}>—</span>
        ),
    },
    {
      title: "Phase since",
      key: "phaseSince",
      width: 130,
      render: (_, lead) => {
        const d =
          lead.activationPhase === "first_sale" ? lead.firstSaleAt :
          lead.activationPhase === "event_created" ? lead.eventCreatedAt :
          lead.stage === "purchased" ? lead.planPurchasedAt :
          lead.brandPageCreatedAt;
        if (!d) return <span style={{ color: "#cbd5e1" }}>—</span>;
        return (
          <Tooltip title={dayjs(d).format("DD MMM YYYY HH:mm")}>
            <span style={{ fontSize: 12, color: "#64748b" }}>{relativeDays(d)}</span>
          </Tooltip>
        );
      },
    },
    {
      title: "Idle",
      dataIndex: "updatedAt",
      key: "updatedAt",
      width: 90,
      render: (d) => {
        const days = dayjs().diff(dayjs(d), "day");
        const danger = days > 30;
        return (
          <Tooltip title={dayjs(d).format("DD MMM YYYY HH:mm")}>
            <span style={{ fontSize: 12, fontWeight: 600, color: danger ? "#dc2626" : "#64748b" }}>
              {days}d
            </span>
          </Tooltip>
        );
      },
      sorter: (a, b) => new Date(a.updatedAt) - new Date(b.updatedAt),
    },
  ];

  if (error) {
    return <Page><Alert type="error" message={error} showIcon /></Page>;
  }

  return (
    <Page>
      <Header>
        <h1>Post-Onboard Contacts</h1>
        <p>Onboarded → Brand Page → Event Created → First Sale → Plan Purchased</p>
      </Header>

      <StatsStrip>
        {GROUPS.map((g) => (
          <StatPill
            key={g.id}
            $active={activeGroup === g.id}
            $accent={g.accent}
            onClick={() => { setActiveGroup(g.id); setPagination((p) => ({ ...p, current: 1 })); }}
          >
            <div className="dot" />
            <span className="label">{g.label}</span>
            <span className="value">{groupTotals[g.id] ?? 0}</span>
          </StatPill>
        ))}
      </StatsStrip>

      <FilterBar>
        <Input
          prefix={<SearchOutlined style={{ color: "#94a3b8" }} />}
          placeholder="Search name, phone, email, city…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onPressEnter={() => fetchPage(1, pagination.pageSize, search)}
          allowClear
          onClear={() => { setSearch(""); fetchPage(1, pagination.pageSize, ""); }}
          style={{ width: 320 }}
        />
        <ReloadOutlined
          onClick={() => fetchPage(pagination.current, pagination.pageSize)}
          style={{ cursor: "pointer", color: "#64748b", fontSize: 16, padding: "0 6px" }}
        />
        <div style={{ marginLeft: "auto", fontSize: 12, color: "#94a3b8" }}>
          Showing {leads.length} of {pagination.total.toLocaleString()} contacts
          {group.description && <> · {group.description}</>}
        </div>
      </FilterBar>

      <TableCard>
        <Spin spinning={loading}>
          <Table
            columns={columns}
            dataSource={leads}
            rowKey="leadId"
            size="middle"
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              total: pagination.total,
              showSizeChanger: true,
              pageSizeOptions: [25, 50, 100, 200],
              showTotal: (t, range) => `${range[0]}-${range[1]} of ${t}`,
            }}
            onChange={(pag) => fetchPage(pag.current, pag.pageSize)}
            onRow={(rec) => ({
              onClick: () => setSelectedLeadId(rec.leadId),
              style: { cursor: "pointer" },
            })}
          />
        </Spin>
      </TableCard>

      <LeadDrawer
        leadId={selectedLeadId}
        open={!!selectedLeadId}
        onClose={() => setSelectedLeadId(null)}
        onUpdate={() => fetchPage(pagination.current, pagination.pageSize)}
      />
    </Page>
  );
};

export default PostOnboardBoard;
